// Project-overview notebook — the editable "Overview" tab of the admin console.
// A persisted checklist the admin AND Claude Code sessions add items to and mark
// done (sessions POST /admin/notebook with author="claude"). Sits alongside the
// read-only auto readiness checks; here everything is hand-written and editable.
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createNotebookItem,
  deleteNotebookItem,
  listNotebook,
  updateNotebookItem,
  type NotebookItem,
  type NotebookStatus,
} from "@/api/admin-notebook";
import { Alert, Button, Input, Spinner, cx } from "@/components/ui";

const STATUS_ORDER: NotebookStatus[] = ["todo", "in_progress", "done"];

const STATUS_LABEL: Record<NotebookStatus, string> = {
  todo: "To do",
  in_progress: "In progress",
  done: "Done",
};

// Click the leading control to advance the item through the lifecycle.
const NEXT_STATUS: Record<NotebookStatus, NotebookStatus> = {
  todo: "in_progress",
  in_progress: "done",
  done: "todo",
};

function authorChip(author: string): string {
  return author === "claude"
    ? "bg-violet-50 text-violet-700 ring-violet-200"
    : "bg-black/5 text-ink-soft ring-black/10";
}

export function AdminNotebookSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-notebook"], queryFn: listNotebook });
  const [title, setTitle] = useState("");
  const [section, setSection] = useState("");

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin-notebook"] });

  const createMut = useMutation({
    mutationFn: () =>
      createNotebookItem({
        title: title.trim(),
        section: section.trim() || null,
      }),
    onSuccess: () => {
      setTitle("");
      setSection("");
      invalidate();
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: NotebookStatus }) =>
      updateNotebookItem(id, { status }),
    onSuccess: invalidate,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteNotebookItem(id),
    onSuccess: invalidate,
  });

  const groups = useMemo(() => groupByStatus(q.data ?? []), [q.data]);
  const openCount = (q.data ?? []).filter((i) => i.status !== "done").length;

  const canAdd = title.trim().length > 0 && !createMut.isPending;

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-ink">{t("Project overview")}</h2>
          <p className="mt-1 text-sm text-ink-faint">
            {t(
              "A living notebook. Add items here or from a Claude session; tick them off as they land.",
            )}
          </p>
        </div>
        {q.data ? (
          <span
            className={cx(
              "shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ring-1",
              openCount === 0
                ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                : "bg-amber-50 text-amber-700 ring-amber-200",
            )}
          >
            {openCount === 0
              ? t("All done")
              : t("{{count}} open", { count: openCount })}
          </span>
        ) : null}
      </div>

      {/* Add row */}
      <form
        className="mt-4 flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (canAdd) createMut.mutate();
        }}
      >
        <div className="min-w-[12rem] flex-1">
          <Input
            label=""
            placeholder={t("Add a to-do, note, or decision…")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="w-40">
          <Input
            label=""
            placeholder={t("Section (optional)")}
            value={section}
            onChange={(e) => setSection(e.target.value)}
          />
        </div>
        <Button type="submit" disabled={!canAdd} loading={createMut.isPending}>
          {t("Add")}
        </Button>
      </form>

      {createMut.isError && (
        <div className="mt-2">
          <Alert>{t("Couldn't add that item.")}</Alert>
        </div>
      )}

      {q.isLoading ? (
        <div className="flex justify-center py-8 text-brand-600">
          <Spinner className="h-6 w-6" />
        </div>
      ) : q.isError ? (
        <div className="mt-4">
          <Alert>{t("Couldn't load the notebook. Try again.")}</Alert>
        </div>
      ) : (q.data ?? []).length === 0 ? (
        <p className="mt-6 text-center text-sm text-ink-faint">
          {t("Nothing here yet. Add the first item above.")}
        </p>
      ) : (
        <div className="mt-5 space-y-5">
          {groups.map(([statusKey, items]) => (
            <div key={statusKey}>
              <div className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
                {t(STATUS_LABEL[statusKey])} · {items.length}
              </div>
              <ul className="mt-2 space-y-2">
                {items.map((it) => (
                  <NotebookRow
                    key={it.id}
                    item={it}
                    busy={updateMut.isPending || deleteMut.isPending}
                    onCycle={(id) =>
                      updateMut.mutate({ id, status: NEXT_STATUS[it.status] })
                    }
                    onToggleDone={(id) =>
                      updateMut.mutate({
                        id,
                        status: it.status === "done" ? "todo" : "done",
                      })
                    }
                    onDelete={(id) => {
                      if (window.confirm(t("Delete this item?"))) deleteMut.mutate(id);
                    }}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NotebookRow({
  item,
  busy,
  onCycle,
  onToggleDone,
  onDelete,
}: {
  item: NotebookItem;
  busy: boolean;
  onCycle: (id: string) => void;
  onToggleDone: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation();
  const done = item.status === "done";
  return (
    <li className="flex items-start gap-3 rounded-xl border border-black/5 bg-white p-3">
      {/* Leading checkbox — toggles done. Long-press-free; the status pill cycles. */}
      <button
        type="button"
        disabled={busy}
        onClick={() => onToggleDone(item.id)}
        aria-label={done ? t("Mark as to do") : t("Mark as done")}
        className={cx(
          "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors disabled:opacity-50",
          done
            ? "border-emerald-500 bg-emerald-500 text-white"
            : "border-black/20 text-transparent hover:border-brand-400",
        )}
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5">
          <path
            fillRule="evenodd"
            d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4l3.3 3.3 6.8-6.8a1 1 0 011.4 0z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cx(
              "text-sm font-semibold",
              done ? "text-ink-faint line-through" : "text-ink",
            )}
          >
            {item.title}
          </span>
          {/* Status pill — click to advance todo → in progress → done → todo. */}
          <button
            type="button"
            disabled={busy}
            onClick={() => onCycle(item.id)}
            className={cx(
              "rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 transition-colors disabled:opacity-50",
              item.status === "done"
                ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                : item.status === "in_progress"
                  ? "bg-amber-50 text-amber-700 ring-amber-200"
                  : "bg-black/5 text-ink-soft ring-black/10",
            )}
          >
            {t(STATUS_LABEL[item.status])}
          </button>
          <span
            className={cx(
              "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ring-1",
              authorChip(item.author),
            )}
          >
            {item.author}
          </span>
          {item.section ? (
            <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-700 ring-1 ring-brand-200">
              {item.section}
            </span>
          ) : null}
        </div>
        {item.body ? (
          <p className="mt-0.5 whitespace-pre-wrap text-sm text-ink-soft">{item.body}</p>
        ) : null}
      </div>

      <Button variant="ghost" size="sm" disabled={busy} onClick={() => onDelete(item.id)}>
        {t("Delete")}
      </Button>
    </li>
  );
}

// Group into To do / In progress / Done, preserving server order within a group.
function groupByStatus(items: NotebookItem[]): [NotebookStatus, NotebookItem[]][] {
  const map: Record<NotebookStatus, NotebookItem[]> = {
    todo: [],
    in_progress: [],
    done: [],
  };
  for (const it of items) map[it.status].push(it);
  return STATUS_ORDER.filter((s) => map[s].length > 0).map((s) => [s, map[s]]);
}
